/* eslint-disable react/prop-types */
import { Descriptions } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as permissionKind from '../../../../../../shared/permissions';
import { type ApplicationListEndpointSpecsFragment } from '../../../../../api/fragments';
import {
    CreateApplicationListEndpointDocument,
    type CreateApplicationListEndpointMutation,
    type CreateApplicationListEndpointMutationVariables,
} from '../../../../../api/mutations/createApplicationListEndpoint';
import {
    UpdateApplicationListEndpointDocument,
    type UpdateApplicationListEndpointMutation,
    type UpdateApplicationListEndpointMutationVariables,
} from '../../../../../api/mutations/updateApplicationListEndpoint';
import { type ApplicationListEndpointSettings, ApplicationStage } from '../../../../../api/types';
import { useCompanyContext } from '../../../../../components/contexts/CompanyContextManager';
import InputField from '../../../../../components/fields/InputField';
import SelectField, { type SelectFieldProps } from '../../../../../components/fields/SelectField';
import hasPermissions from '../../../../../utilities/hasPermissions';
import useSystemOptions from '../../../../../utilities/useSystemOptions';
import { type EndpointImplementation } from './types';

const StyledBulletsList = styled.ul`
    margin-bottom: 0;
`;

const implementation: EndpointImplementation<ApplicationListEndpointSpecsFragment, ApplicationListEndpointSettings> = {
    getTitle: t => t('routerDetails:endpoints.ApplicationListEndpoint.title'),
    ViewPanelComponent: ({ endpoint }) => (
        <Descriptions size="small" bordered>
            <Descriptions.Item label="Modules">
                {endpoint.applicationModules.length > 1 ? (
                    <StyledBulletsList>
                        {endpoint.applicationModules.map(module => (
                            <li key={module.id}>{module.displayName}</li>
                        ))}
                    </StyledBulletsList>
                ) : (
                    <div>{endpoint.applicationModules[0].displayName} </div>
                )}
            </Descriptions.Item>
            <Descriptions.Item label="Application Stage">{endpoint.applicationStage}</Descriptions.Item>
        </Descriptions>
    ),
    getIsPrivate: () => true,
    FormComponent: ({ router }) => {
        const { t } = useTranslation(['routerDetails']);
        const { companies } = useCompanyContext();

        const options = useMemo(
            () =>
                router.company.modules.reduce<SelectFieldProps['options']>((acc, module) => {
                    if (
                        [
                            'EventApplicationModule',
                            'StandardApplicationModule',
                            'ConfiguratorModule',
                            'FinderApplicationPublicModule',
                            'FinderApplicationPrivateModule',
                        ].includes(module.__typename)
                    ) {
                        return [...acc, { value: module.id, label: module.displayName }];
                    }

                    return acc;
                }, []),
            [router]
        );

        const { ApplicationStageOptions } = useSystemOptions();
        const availableApplicationStageOptions = useMemo(
            () =>
                ApplicationStageOptions.map(appStage => {
                    switch (appStage.value) {
                        case ApplicationStage.Financing:
                            if (
                                companies.some(
                                    company =>
                                        company.availableModules.hasBanks &&
                                        company.availableModules.hasApplicationModules &&
                                        hasPermissions(company.permissions, [permissionKind.viewFinanceApplications])
                                )
                            ) {
                                return appStage;
                            }

                            return null;

                        case ApplicationStage.Lead:
                            if (
                                companies.some(
                                    company =>
                                        company.availableModules.hasApplicationModules &&
                                        hasPermissions(company.permissions, [permissionKind.viewLeads])
                                )
                            ) {
                                return appStage;
                            }

                            return null;

                        case ApplicationStage.Reservation:
                            if (
                                companies.some(
                                    company =>
                                        company.availableModules.hasApplicationModules &&
                                        company.availableModules.hasPaymentModules &&
                                        hasPermissions(company.permissions, [permissionKind.viewReservations])
                                )
                            ) {
                                return appStage;
                            }

                            return null;

                        case ApplicationStage.Appointment:
                            if (
                                companies.some(
                                    company =>
                                        company.availableModules.hasAppointmentModules &&
                                        hasPermissions(company.permissions, [permissionKind.viewAppointments])
                                )
                            ) {
                                return appStage;
                            }

                            return null;

                        case ApplicationStage.Insurance:
                            if (
                                companies.some(
                                    company =>
                                        company.availableModules.hasApplicationModules &&
                                        company.availableModules.hasInsuranceModules &&
                                        hasPermissions(company.permissions, [permissionKind.viewInsuranceApplications])
                                )
                            ) {
                                return appStage;
                            }

                            return null;

                        default:
                            return null;
                    }
                }).filter(Boolean),
            [ApplicationStageOptions, companies]
        );

        return (
            <>
                <InputField
                    {...t('routerDetails:endpoints.fields.pathname', { returnObjects: true })}
                    name="pathname"
                />
                <SelectField
                    {...t('routerDetails:endpoints.fields.applicationModuleIds', { returnObjects: true })}
                    mode="multiple"
                    name="applicationModuleIds"
                    options={options}
                    showSearch
                />
                <InputField {...t('routerDetails:endpoints.fields.title', { returnObjects: true })} name="title" />
                <SelectField
                    {...t('routerDetails:endpoints.fields.applicationStage', { returnObjects: true })}
                    name="applicationStage"
                    options={availableApplicationStageOptions}
                    showSearch
                />
            </>
        );
    },
    getFormInitialValues: endpoint => ({
        pathname: endpoint.pathname,
        applicationModuleIds: endpoint.applicationModules.map(module => module.id),
        applicationStage: endpoint.applicationStage,
        title: endpoint.title,
    }),
    submitCreation: (apolloClient, routerId, values) =>
        apolloClient.mutate<CreateApplicationListEndpointMutation, CreateApplicationListEndpointMutationVariables>({
            mutation: CreateApplicationListEndpointDocument,
            variables: { routerId, settings: values },
        }),
    submitUpdate: (apolloClient, routerId, endpointId, values) =>
        apolloClient.mutate<UpdateApplicationListEndpointMutation, UpdateApplicationListEndpointMutationVariables>({
            mutation: UpdateApplicationListEndpointDocument,
            variables: { routerId, endpointId, settings: values },
        }),
};

export default implementation;
